import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { workItemMasterApi, type WorkItemMaster, type WorkItemMasterInput } from "../api/workItemMasterApi.js";
import type { WorkItemInput } from "../api/workItemsApi.js";
import { btnStyle } from "../App.js";
import { toast } from "../components/Toast.js";
import { masterItemAmount } from "../components/work-items/masterOptions.js";
import { useScreenView } from "../lib/analytics.js";
import {
  PROFESSION_LABEL,
  WORK_CATEGORY_LABEL,
  getDefaultIsPassThrough,
  getDefaultTaxCategory,
  getDefaultWithholdingTarget,
} from "../lib/workItemConstants.js";

const PROFESSIONS = ["", "judicial", "administrative", "surveyor", "common", "other"] as const;
const CATEGORIES = ["fee", "registration_tax", "stamp", "postage", "pass_through", "other"] as const;
const PROFESSION_DOC_TYPES: Record<string, string[]> = {
  judicial: ["judicial_invoice", "estimate"],
  administrative: ["administrative_invoice", "estimate"],
  surveyor: ["surveyor_invoice", "estimate"],
  common: ["judicial_invoice", "estimate"],
  other: ["estimate"],
};

export function WorkItemMasterPage() {
  useScreenView("work_item_master");
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [profession, setProfession] = useState("");
  const [q, setQ] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [itemProfession, setItemProfession] = useState("judicial");
  const [category, setCategory] = useState("fee");
  const [title, setTitle] = useState("");
  const [unitPrice, setUnitPrice] = useState(0);
  const [pinned, setPinned] = useState(false);

  const query = useQuery({
    queryKey: ["work-item-master", profession, q],
    queryFn: () => workItemMasterApi.list({ profession: profession || undefined, q: q || undefined }),
  });

  const masters = query.data?.data ?? [];
  const grouped = useMemo(() => {
    return masters.reduce<Record<string, typeof masters>>((acc, item) => {
      (acc[item.profession] ??= []).push(item);
      return acc;
    }, {});
  }, [masters]);

  const createMut = useMutation({
    mutationFn: (body: WorkItemMasterInput) => workItemMasterApi.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["work-item-master"] });
      setName("");
      setDescription("");
      setTitle("");
      setUnitPrice(0);
      setPinned(false);
      toast("業務マスタを作成しました");
    },
    onError: (e) => toast(e.message, "error"),
  });

  const pinMut = useMutation({
    mutationFn: (id: string) => workItemMasterApi.pin(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["work-item-master"] });
      toast("ピン留めを更新しました");
    },
    onError: (e) => toast(e.message, "error"),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => workItemMasterApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["work-item-master"] });
      toast("業務マスタを削除しました");
    },
    onError: (e) => toast(e.message, "error"),
  });

  function handleCreate(event: React.FormEvent) {
    event.preventDefault();
    if (!name.trim() || !title.trim()) return;
    const inputProfession = itemProfession as "judicial" | "administrative" | "surveyor" | "common" | "other";
    const inputCategory = category as "fee" | "registration_tax" | "stamp" | "postage" | "pass_through" | "other";
    createMut.mutate({
      name: name.trim(),
      description: description.trim() || null,
      profession: inputProfession,
      is_pinned: pinned,
      default_items: [{
        profession: inputProfession,
        category: inputCategory,
        title: title.trim(),
        quantity: 1,
        unit_price: Math.floor(unitPrice),
        tax_category: getDefaultTaxCategory(inputCategory),
        withholding_target: getDefaultWithholdingTarget(inputCategory, inputProfession),
        is_pass_through: getDefaultIsPassThrough(inputCategory),
        target_doc_types: PROFESSION_DOC_TYPES[inputProfession] ?? ["estimate"],
      }],
    });
  }

  const inputStyle: React.CSSProperties = {
    height: 34,
    border: "1px solid #cbd5e1",
    borderRadius: 8,
    padding: "0 10px",
    fontSize: 13,
    fontFamily: "inherit",
    background: "#fff",
  };

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 800, margin: 0 }}>業務マスタ</h1>
          <p style={{ fontSize: 13, color: "#64748b", margin: "6px 0 0" }}>
            案件画面でワンクリック適用する定型業務を管理します。
          </p>
          <p style={{ fontSize: 12, color: "#94a3b8", margin: "4px 0 0", maxWidth: 620, lineHeight: 1.6 }}>
            ここで登録するのは請求書・見積書へ転記される明細です。
            土地計算書・建物計算書の金額は業務マスタではなく
            <strong style={{ color: "#64748b" }}>料金表</strong>
            （面積・数量から自動計算）で決まるため、業務マスタに登録する必要はありません。
          </p>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <Link
          to="/settings/professions"
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            fontSize: 13, fontWeight: 700, color: "#0f766e", textDecoration: "none",
          }}
        >
          資格者・登録番号の管理 ›
        </Link>
      </div>

      <section style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: 16, marginBottom: 16 }}>
        <form onSubmit={handleCreate} style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr 1fr 120px 90px", gap: 10, alignItems: "end" }}>
          <label style={labelStyle}>
            マスタ名
            <input style={inputStyle} value={name} placeholder="例: 所有権移転登記（売買）" onChange={(e) => setName(e.target.value)} />
          </label>
          <label style={labelStyle}>
            業種
            <select style={inputStyle} value={itemProfession} onChange={(e) => setItemProfession(e.target.value)}>
              {PROFESSIONS.filter(Boolean).map((p) => <option key={p} value={p}>{PROFESSION_LABEL[p]}</option>)}
            </select>
          </label>
          <label style={labelStyle}>
            初期業務
            <input style={inputStyle} value={title} placeholder="例: 登記報酬" onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label style={labelStyle}>
            単価
            <input style={{ ...inputStyle, textAlign: "right" }} type="number" min={0} value={unitPrice} onChange={(e) => setUnitPrice(Number(e.target.value))} />
          </label>
          <button style={btnStyle("primary")} disabled={createMut.isPending}>作成</button>
          <label style={labelStyle}>
            カテゴリ
            <select style={inputStyle} value={category} onChange={(e) => setCategory(e.target.value)}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{WORK_CATEGORY_LABEL[c]}</option>)}
            </select>
          </label>
          <label style={{ ...labelStyle, gridColumn: "span 3" }}>
            説明
            <input style={inputStyle} value={description} placeholder="案件画面で選ぶ時の補足" onChange={(e) => setDescription(e.target.value)} />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, height: 34, fontSize: 13, color: "#334155", fontWeight: 700 }}>
            <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} />
            ピン留め
          </label>
        </form>
      </section>

      <section style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14 }}>
        <select style={inputStyle} value={profession} onChange={(e) => setProfession(e.target.value)}>
          {PROFESSIONS.map((p) => <option key={p} value={p}>{p ? PROFESSION_LABEL[p] : "全業種"}</option>)}
        </select>
        <input style={{ ...inputStyle, width: 260 }} value={q} placeholder="マスタ名・説明で検索" onChange={(e) => setQ(e.target.value)} />
        <span style={{ fontSize: 12, color: "#64748b" }}>{query.isLoading ? "読み込み中..." : `${masters.length}件`}</span>
      </section>

      <div style={{ display: "grid", gap: 14 }}>
        {Object.entries(grouped).map(([group, items]) => (
          <section key={group} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
            <div style={{ padding: "10px 14px", borderBottom: "1px solid #e2e8f0", background: "#f8fafc", fontSize: 13, fontWeight: 900, color: "#334155" }}>
              {PROFESSION_LABEL[group as keyof typeof PROFESSION_LABEL]}
            </div>
            <div>
              {items.map((item) => (
                <div key={item.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto auto", gap: 12, alignItems: "center", padding: "12px 14px" }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 14, fontWeight: 800, color: "#0f172a" }}>{item.name}</span>
                        {item.source === "builtin" && <span style={pillStyle}>標準</span>}
                        {item.is_pinned && <span style={pillStyle}>ピン留め</span>}
                      </div>
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>
                        {item.description || "説明なし"} / {item.default_items.length}件 / 使用 {item.usage_count}回
                      </div>
                      <div style={{ fontSize: 12, color: "#334155", marginTop: 3 }}>
                        {summarizeItems(item.default_items)}
                      </div>
                    </div>
                    <button
                      style={btnStyle("outline")}
                      onClick={() => setEditingId(editingId === item.id ? null : item.id)}
                    >
                      {editingId === item.id ? "閉じる" : "編集"}
                    </button>
                    <button style={btnStyle("outline")} onClick={() => pinMut.mutate(item.id)}>{item.is_pinned ? "ピン解除" : "ピン留め"}</button>
                    <button style={btnStyle("danger")} onClick={() => deleteMut.mutate(item.id)}>削除</button>
                  </div>
                  {editingId === item.id && (
                    <MasterEditForm
                      key={`${item.id}:${item.updated_at}`}
                      master={item}
                      onClose={() => setEditingId(null)}
                    />
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}
        {!query.isLoading && masters.length === 0 && (
          <div style={{ padding: 28, textAlign: "center", color: "#94a3b8", background: "#fff", border: "1px dashed #cbd5e1", borderRadius: 8 }}>
            業務マスタがありません。
          </div>
        )}
      </div>
    </div>
  );
}

// ── 既存マスタの編集（単価・業務名・カテゴリ等）─────────────────────────────
// API: PUT /api/work-item-master/:id（default_items を渡すと明細ごと差し替わる）
// 束（複数明細）のマスタでも 1 行ずつ単価を編集できるようにする。

const editInputStyle: React.CSSProperties = {
  height: 32,
  border: "1px solid #cbd5e1",
  borderRadius: 6,
  padding: "0 8px",
  fontSize: 13,
  fontFamily: "inherit",
  background: "#fff",
  color: "#0f172a",
  minWidth: 0,
};

function summarizeItems(items: WorkItemInput[]): string {
  return items
    .map((it) => `${it.title} ¥${masterItemAmount(it).toLocaleString("ja-JP")}`)
    .join(" ／ ");
}

function MasterEditForm({ master, onClose }: { master: WorkItemMaster; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(master.name);
  const [description, setDescription] = useState(master.description ?? "");
  const [profession, setProfession] = useState(master.profession);
  const [isPinned, setIsPinned] = useState(master.is_pinned);
  const [items, setItems] = useState<WorkItemInput[]>(() =>
    JSON.parse(JSON.stringify(master.default_items)) as WorkItemInput[],
  );

  const updateMut = useMutation({
    mutationFn: (body: Partial<WorkItemMasterInput>) => workItemMasterApi.update(master.id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["work-item-master"] });
      toast("業務マスタを更新しました");
      onClose();
    },
    onError: (e) => toast(e.message, "error"),
  });

  function patchItem(index: number, patch: Partial<WorkItemInput>) {
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, ...patch } : it)));
  }

  /** カテゴリ変更時は税区分・源泉対象・立替フラグを既定値へ追随させる（新規作成時と同じ導出） */
  function changeCategory(index: number, category: string) {
    const itemProfession = items[index]?.profession ?? profession;
    patchItem(index, {
      category,
      tax_category: getDefaultTaxCategory(category),
      withholding_target: getDefaultWithholdingTarget(category, itemProfession),
      is_pass_through: getDefaultIsPassThrough(category),
    });
  }

  /**
   * 業種変更時は各明細の業種・源泉対象を揃える（新規作成時と同じ導出）。
   * target_doc_types はここで上書きしない: 「どの書類にも出さない」（[]）等のユーザー設定が
   * 業種変更だけで消えてしまうため（レビュー指摘 F2）。
   */
  function changeProfession(next: string) {
    setProfession(next);
    setItems((prev) => prev.map((it) => ({
      ...it,
      profession: next,
      withholding_target: getDefaultWithholdingTarget(it.category, next),
    })));
  }

  function handleSave() {
    if (!name.trim()) { toast("マスタ名は必須です", "error"); return; }
    if (items.length === 0) { toast("明細は1件以上必要です", "error"); return; }
    if (items.some((it) => !it.title.trim())) { toast("業務名は必須です", "error"); return; }
    updateMut.mutate({
      name: name.trim(),
      description: description.trim() || null,
      profession,
      is_pinned: isPinned,
      default_items: items.map((it) => {
        const quantity = Math.max(0, Math.floor(it.quantity ?? 1));
        const unitPrice = Math.max(0, Math.floor(it.unit_price ?? 0));
        return { ...it, title: it.title.trim(), quantity, unit_price: unitPrice, amount: quantity * unitPrice };
      }),
    });
  }

  return (
    <div style={{ padding: "0 14px 16px", background: "#f8fafc", borderTop: "1px dashed #cbd5e1" }}>
      <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr", gap: 10, padding: "12px 0" }}>
        <label style={labelStyle}>
          マスタ名
          <input style={editInputStyle} value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label style={labelStyle}>
          業種
          <select style={editInputStyle} value={profession} onChange={(e) => changeProfession(e.target.value)}>
            {PROFESSIONS.filter(Boolean).map((p) => <option key={p} value={p}>{PROFESSION_LABEL[p]}</option>)}
          </select>
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 8, height: 32, alignSelf: "end", fontSize: 13, color: "#334155", fontWeight: 700 }}>
          <input type="checkbox" checked={isPinned} onChange={(e) => setIsPinned(e.target.checked)} />
          ピン留め
        </label>
        <label style={{ ...labelStyle, gridColumn: "span 3" }}>
          説明
          <input style={editInputStyle} value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
      </div>

      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={editThStyle}>業務名</th>
            <th style={{ ...editThStyle, width: 130 }}>カテゴリ</th>
            <th style={{ ...editThStyle, width: 80 }}>数量</th>
            <th style={{ ...editThStyle, width: 120 }}>単価（円）</th>
            <th style={{ ...editThStyle, width: 110 }}>金額</th>
            <th style={{ ...editThStyle, width: 60 }} />
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i} style={{ borderTop: "1px solid #e2e8f0" }}>
              <td style={{ padding: "4px 6px" }}>
                <input
                  style={{ ...editInputStyle, width: "100%" }}
                  value={it.title}
                  onChange={(e) => patchItem(i, { title: e.target.value })}
                />
              </td>
              <td style={{ padding: "4px 6px" }}>
                <select style={{ ...editInputStyle, width: "100%" }} value={it.category} onChange={(e) => changeCategory(i, e.target.value)}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{WORK_CATEGORY_LABEL[c]}</option>)}
                </select>
              </td>
              <td style={{ padding: "4px 6px" }}>
                <input
                  type="number"
                  min={0}
                  style={{ ...editInputStyle, width: "100%", textAlign: "right" }}
                  value={it.quantity ?? 1}
                  onChange={(e) => patchItem(i, { quantity: Math.max(0, Math.floor(Number(e.target.value) || 0)) })}
                />
              </td>
              <td style={{ padding: "4px 6px" }}>
                <input
                  type="number"
                  min={0}
                  style={{ ...editInputStyle, width: "100%", textAlign: "right" }}
                  value={it.unit_price ?? 0}
                  onChange={(e) => patchItem(i, { unit_price: Math.max(0, Math.floor(Number(e.target.value) || 0)) })}
                />
              </td>
              <td style={{ padding: "4px 6px", textAlign: "right", fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
                ¥{((it.quantity ?? 1) * (it.unit_price ?? 0)).toLocaleString("ja-JP")}
              </td>
              <td style={{ padding: "4px 6px" }}>
                <button
                  style={{ ...btnStyle("ghost"), color: "#dc2626" }}
                  disabled={items.length <= 1}
                  onClick={() => setItems((prev) => prev.filter((_, j) => j !== i))}
                >
                  削除
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
        <button
          style={btnStyle("outline")}
          onClick={() => setItems((prev) => [...prev, {
            profession,
            category: "fee",
            title: "",
            quantity: 1,
            unit_price: 0,
            tax_category: getDefaultTaxCategory("fee"),
            withholding_target: getDefaultWithholdingTarget("fee", profession),
            is_pass_through: getDefaultIsPassThrough("fee"),
            target_doc_types: PROFESSION_DOC_TYPES[profession] ?? ["estimate"],
          }])}
        >
          明細を追加
        </button>
        <div style={{ flex: 1 }} />
        <button style={btnStyle("ghost")} onClick={onClose}>キャンセル</button>
        <button style={btnStyle("primary")} onClick={handleSave} disabled={updateMut.isPending}>
          {updateMut.isPending ? "保存中…" : "保存"}
        </button>
      </div>
    </div>
  );
}

const editThStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: "#64748b",
  textAlign: "left",
  padding: "4px 6px",
  whiteSpace: "nowrap",
};

const labelStyle: React.CSSProperties = {
  display: "grid",
  gap: 4,
  fontSize: 12,
  color: "#64748b",
  fontWeight: 800,
};

const pillStyle: React.CSSProperties = {
  border: "1px solid #ccfbf1",
  background: "#f0fdfa",
  color: "#0f766e",
  borderRadius: 999,
  padding: "2px 7px",
  fontSize: 11,
  fontWeight: 800,
};
